/**
 * Cockpit sign-in: the session token, and the calls the login screen makes.
 *
 * What changed and why: login used to be a shared 3-word password checked entirely in
 * the browser, with the API wide open behind it. Once Revenue Challenge applicants
 * started using the cockpit alongside the team, that stopped working: a shared password
 * can't be taken away from one person, and a browser-only check never protected the API
 * at all. Now you prove you own an email address that's on the roster, and every request
 * carries a session token the server can revoke.
 *
 * The token is the ONE credential: `Authorization: Bearer <token>` on API calls, `?t=`
 * on websockets and download links (which can't set headers).
 */
import axios, { AxiosInstance } from 'axios';

const TOKEN_KEY = 'cockpit_session_token';

export interface CockpitUser {
  id: number;
  email: string;
  name: string | null;
  role: 'admin' | 'team' | 'guest';
  rep_slug: string | null;
  active: boolean;
  expires_at: string | null;
  invited_by?: string | null;
  note?: string | null;
  created_at?: string | null;
  last_seen_at?: string | null;
  active_sessions?: number;
}

// ---- token storage ---------------------------------------------------------

export const getToken = (): string => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const setToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore: a browser with storage blocked still works for this session */
  }
};

export const clearToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
    // The old browser-only login flag, from before there was a real session.
    localStorage.removeItem('ai_feedback_auth');
  } catch {
    /* ignore */
  }
};

/** Headers for a raw fetch() (the streaming chat calls and file downloads). */
export const authHeaders = (extra: Record<string, string> = {}): Record<string, string> => {
  const token = getToken();
  return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
};

/** Append the token to a URL a browser opens directly (websockets, download links). */
export const withTokenParam = (url: string): string => {
  const token = getToken();
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}t=${encodeURIComponent(token)}`;
};

/**
 * A one-time sign-in link (scripts/manage_access.py login-link) puts the token in the
 * URL. Consume it on boot and strip it from the address bar so it isn't left in
 * history or copied out of the location field.
 */
export const consumeTokenFromUrl = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return false;
    setToken(token);
    params.delete('token');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
    return true;
  } catch {
    return false;
  }
};

// ---- attaching the token to an axios instance -------------------------------

// A dead session (revoked, expired, or the person deactivated) comes back as a 401.
// Drop the token and tell the app to show the login screen instead of leaving the page
// silently broken.
export const SESSION_EXPIRED_EVENT = 'cockpit:session-expired';

/**
 * Give an axios instance the session token on every request, and the 401 handling that
 * sends a dead session back to the login screen.
 *
 * EVERY axios instance that talks to /api must call this. The cockpit has more than one
 * (services/api.ts, chat.service.ts, uploads.service.ts): when the API gate went in on
 * 2026-08-21 only api.ts got interceptors, so every /api/chat/* call went out
 * unauthenticated and the chat dock came up empty with no history. One helper, applied
 * everywhere, is what keeps that from happening again.
 */
export const attachAuth = (instance: AxiosInstance): void => {
  instance.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        clearToken();
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      return Promise.reject(error);
    }
  );
};

// ---- API -------------------------------------------------------------------

const authApi = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

export interface RequestCodeResult {
  sent: boolean;
  reason?: string;
  message?: string;
  expires_in_minutes?: number;
}

export const requestLoginCode = async (email: string): Promise<RequestCodeResult> => {
  const response = await authApi.post('/auth/request-code', { email });
  return response.data;
};

export interface VerifyResult {
  token: string;
  user: CockpitUser;
  needs_profile: boolean;
}

export const verifyLoginCode = async (email: string, code: string): Promise<VerifyResult> => {
  const response = await authApi.post('/auth/verify', { email, code });
  setToken(response.data.token);
  return response.data;
};

export interface MeResult {
  user: CockpitUser | null;
  auth_enabled: boolean;
  needs_profile?: boolean;
}

export const fetchMe = async (): Promise<MeResult> => {
  const response = await authApi.get('/auth/me', { headers: authHeaders() });
  return response.data;
};

/** One selectable identity on the "You are:" list (see /api/auth/people). */
export interface RosterPerson {
  name: string;
  email: string;
  rep_slug: string | null;
  is_rep: boolean;
}

/**
 * The maintained people list behind the identity selector. Derived server-side from the
 * access roster, so a teammate who signs in with a team email is selectable straight
 * away and nobody has to edit a hardcoded list of names.
 */
export const fetchPeople = async (): Promise<RosterPerson[]> => {
  const response = await authApi.get('/auth/people', { headers: authHeaders() });
  return response.data?.people || [];
};

export const saveProfile = async (name: string, repSlug?: string | null): Promise<CockpitUser> => {
  const response = await authApi.post(
    '/auth/profile',
    { name, rep_slug: repSlug || null },
    { headers: authHeaders() }
  );
  return response.data.user;
};

export const logout = async (): Promise<void> => {
  try {
    await authApi.post('/auth/logout', {}, { headers: authHeaders() });
  } catch {
    /* signing out locally matters more than the server round trip */
  }
  clearToken();
};

// ---- roster (admin) --------------------------------------------------------

export const listAccessUsers = async (): Promise<CockpitUser[]> => {
  const response = await authApi.get('/auth/users', { headers: authHeaders() });
  return response.data;
};

export interface InvitePayload {
  email: string;
  role: 'admin' | 'team' | 'guest';
  name?: string;
  rep_slug?: string;
  expires_in_days?: number;
  note?: string;
}

export const inviteAccessUser = async (payload: InvitePayload): Promise<CockpitUser> => {
  const response = await authApi.post('/auth/users', payload, { headers: authHeaders() });
  return response.data;
};

export interface UpdateAccessPayload {
  name?: string;
  role?: 'admin' | 'team' | 'guest';
  rep_slug?: string;
  active?: boolean;
  expires_in_days?: number;
  clear_expiry?: boolean;
  note?: string;
}

export const updateAccessUser = async (
  id: number,
  payload: UpdateAccessPayload
): Promise<CockpitUser> => {
  const response = await authApi.patch(`/auth/users/${id}`, payload, { headers: authHeaders() });
  return response.data;
};

export const revokeAccessUserSessions = async (id: number): Promise<number> => {
  const response = await authApi.post(
    `/auth/users/${id}/revoke-sessions`,
    {},
    { headers: authHeaders() }
  );
  return response.data.revoked;
};
